import { useState, type ReactNode } from 'react'
import { cx } from './cx'
import s from './Accordion.module.css'

export interface AccordionItem {
  key: string
  title: ReactNode
  /** right-aligned header slot — a count, progress, badge, etc. */
  meta?: ReactNode
  /** one-line caption under the title */
  subtitle?: ReactNode
  defaultOpen?: boolean
  content: ReactNode
}

/**
 * A collapsible section group (the Kendo ExpansionPanel / PanelBar of the kit) —
 * headers with a title + optional meta slot, bodies that expand in place. The one
 * disclosure primitive: sectioned forms, settings groups, FAQ, the tenders RFx
 * section list. `multiple` (default) lets several open at once; set false for an
 * accordion where opening one closes the rest. Uncontrolled by default (seed with
 * `defaultOpen`); pass `open`/`onToggle` to control it.
 */
export function Accordion({ items, multiple = true, open, onToggle, className }: {
  items: AccordionItem[]
  multiple?: boolean
  open?: string[]
  onToggle?: (key: string, next: boolean) => void
  className?: string
}) {
  const [internal, setInternal] = useState<Set<string>>(
    () => new Set(items.filter((i) => i.defaultOpen).map((i) => i.key)))
  const controlled = open !== undefined
  const isOpen = (k: string) => (controlled ? open!.includes(k) : internal.has(k))

  const toggle = (k: string) => {
    const next = !isOpen(k)
    onToggle?.(k, next)
    if (controlled) return
    setInternal((prev) => {
      const n = new Set(multiple ? prev : [])
      if (n.has(k)) n.delete(k); else n.add(k)
      return n
    })
  }

  return (
    <div className={cx(s.group, className)}>
      {items.map((it) => {
        const on = isOpen(it.key)
        return (
          <section key={it.key} className={cx(s.item, on && s.open)} data-testid={`acc-${it.key}`} data-state={on ? 'open' : 'closed'}>
            <button type="button" className={s.head} onClick={() => toggle(it.key)}
              aria-expanded={on} data-testid={`acc-head-${it.key}`}>
              <span className={s.chev} aria-hidden="true">▸</span>
              <span className={s.titleWrap}>
                <span className={s.title}>{it.title}</span>
                {it.subtitle && <span className={s.subtitle}>{it.subtitle}</span>}
              </span>
              {it.meta != null && <span className={s.meta}>{it.meta}</span>}
            </button>
            {on && <div className={s.body} data-testid={`acc-body-${it.key}`}>{it.content}</div>}
          </section>
        )
      })}
    </div>
  )
}
